import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Avatar,
  TableFooter,
  ButtonGroup,
  Button,
  Skeleton,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  Typography,
  FormControl,
} from "@mui/material";
import theme from "../theme/theme";

import getInitials from "../utils/getInitails";

const stringToColor = (string) => {
  if (!string) return "#9e9e9e";
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

const DEFAULT_PER_PAGE_OPTIONS = [10, 15, 25, 50, 100];

const Table = ({
  columns,
  data,
  loading,
  links,
  setCurrentPage,
  actions = [],
  meta,
  perPage,
  setPerPage,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
}) => {
  const hasActions = actions && actions.length > 0;
  const totalColumns = columns.length + (hasActions ? 1 : 0) + 1;
  const photoColumn = columns.find((col) => col.field === "photo");
  if (photoColumn) {
    data = data.map((row) => ({
      ...row,
      photo: row.photo ? (
        <Avatar src={row.photo} alt={row.name} sx={{ width: 50, height: 50 }} />
      ) : (
        <Avatar
          sx={{
            width: 50,
            height: 50,
            bgcolor: stringToColor(row.name),
            fontSize: 18,
            fontWeight: 600,
          }}
        >
          {getInitials(row.name)}
        </Avatar>
      ),
    }));
  }

  const from = meta?.from ?? 0;
  const to = meta?.to ?? 0;
  const total = meta?.total ?? 0;
  const showRangeInfo = meta && (total > 0 || from || to);
  const showPerPageDropdown = !!setPerPage;
  const showFooterControls = showRangeInfo || showPerPageDropdown || (links && links.length > 0);

  const renderPaginationFooter = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {showPerPageDropdown && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <FormControl size="small">
              <Select
                value={perPage ?? ""}
                onChange={(e) => setPerPage(Number(e.target.value))}
                sx={{ minWidth: 80 }}
              >
                {perPageOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {showRangeInfo && (
          <Typography variant="body2" color="text.secondary">
            {total > 0
              ? `Showing ${from}-${to} of ${total} results`
              : "Showing 0 results"}
          </Typography>
        )}
      </Box>
      {links && links.length > 0 && (
        <ButtonGroup variant="contained" aria-label="Pagination">
          {links.map((link, index) => (
            <Button
              key={index}
              variant={link.active ? "contained" : "outlined"}
              color={link.active ? "primary" : "default"}
              sx={{
                backgroundColor: link.active
                  ? theme.button.activeBackgroundColor
                  : theme.button.backgroundColor,
              }}
              disabled={!link.url}
              onClick={() => {
                setCurrentPage(link.page);
              }}
              borderRadius={theme.button.borderRadius}
            >
              {link.label
                .replace(/&laquo;/g, "«")
                .replace(/&raquo;/g, "»")}
            </Button>
          ))}
        </ButtonGroup>
      )}
    </Box>
  );

  return (
    <TableContainer
      component={Paper}
      sx={{ borderRadius: theme.table.borderRadius }}
    >
      <MuiTable>
        <TableHead sx={{ backgroundColor: theme.table.headerBgColor }}>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>Sr. No</TableCell>
            {columns.map((column) => (
              <TableCell key={column.field}>{column.headerName}</TableCell>
            ))}
            {hasActions && <TableCell>Actions</TableCell>}
          </TableRow>
        </TableHead>
        {loading ? (
          <>
            <TableBody>
              {[...Array(perPage || 10)].map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell>
                    <Skeleton variant="text" width="40%" height={24} />
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.field}>
                      {column.field === "photo" ? (
                        <Skeleton variant="circular" width={50} height={50} />
                      ) : (
                        <Skeleton variant="text" width="80%" height={24} />
                      )}
                    </TableCell>
                  ))}
                  {hasActions && (
                    <TableCell>
                      <ButtonGroup
                        variant="contained"
                        aria-label="Basic button group"
                      >
                        {actions.map((_, idx) => (
                          <Skeleton
                            variant="button"
                            key={idx}
                            width={45}
                            height={32}
                          />
                        ))}
                      </ButtonGroup>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={totalColumns}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      gap: 2,
                    }}
                  >
                    <Skeleton variant="text" width={200} height={24} />
                    <Skeleton
                      variant="rectangular"
                      width={230}
                      height={36}
                      sx={{ borderRadius: 1 }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          </>
        ) : (
          <>
            <TableBody>
              {(data || []).map((row, rowIndex) => (
                <TableRow key={row.id || rowIndex}>
                  <TableCell>{from ? from + rowIndex : rowIndex + 1}</TableCell>
                  {columns.map((column) => {
                    if (column.render) {
                      return (
                        <TableCell key={column.field}>
                          {column.render(row)}
                        </TableCell>
                      );
                    }
                    const value = column.field
                      .split(".")
                      .reduce((acc, part) => acc && acc[part], row);
                    if (column.field === "statusCustom" && value) {
                      return (
                        <TableCell key={column.field}>
                          <Chip
                            label={value.label}
                            color={value.color}
                            size="small"
                            icon={value.icon}
                          />
                        </TableCell>
                      );
                    }
                    return <TableCell key={column.field}>{value}</TableCell>;
                  })}
                  {actions && actions.length > 0 && (
                    <TableCell>
                      <ButtonGroup
                        variant="contained"
                        aria-label="Basic button group"
                      >
                        {actions.map((action, idx) => (
                          <Tooltip
                            key={idx}
                            title={action.tooltip || action.label || "Action"}
                            arrow
                            placement="top"
                          >
                            <Button
                              size="small"
                              color={action.color || "primary"}
                              onClick={() => action.onClick(row)}
                              aria-label={action.tooltip || action.label || "Action"}
                              sx={{
                                backgroundColor:
                                  action.backgroundColor || undefined,
                              }}
                            >
                              {action.icon}
                            </Button>
                          </Tooltip>
                        ))}
                      </ButtonGroup>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
            {showFooterControls && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={totalColumns}>
                    {renderPaginationFooter()}
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </>
        )}
      </MuiTable>
    </TableContainer>
  );
};

export default Table;
